"use client";

import CitationData from "@/src/_types/Article/article-citation-data";
import NextLink from "next/link";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";
import StyledDivider from "@/src/components/common/styled-divider/styled-divider";
import { Box, Link, List, ListItem, Typography } from "@mui/material";
import { formatRelativeTime } from "@/src/utils/dateFormatter";
import { useCallback } from "react";
import {
  getArticleCrossrefUrl,
  getArticleOpenAlexUrl,
  getArticleOpenCitationsUrl,
  getArticleScopusUrl,
} from "@/src/utils/paths";

type CitedBySource = {
  service: string;
  label: string;
  external: boolean;
  href: (args: {
    citation?: CitationData;
    journalAbbr: string;
    articleId: number;
  }) => string;
};

const SOURCES: CitedBySource[] = [
  {
    service: "scopus_by_doi",
    label: "Scopus by DOI",
    external: true,
    href: ({ citation }) => citation?.data?.resultLink ?? "#",
  },
  {
    service: "scopus_by_title",
    label: "Scopus by Title",
    external: true,
    href: ({ citation }) => citation?.data?.resultLink ?? "#",
  },
  {
    service: "scopus_by_title_in_references",
    label: "Scopus by Title (Ref)",
    external: true,
    href: ({ citation, journalAbbr, articleId }) =>
      citation?.count ? getArticleScopusUrl(journalAbbr, articleId) : "#",
  },
  {
    service: "crossref",
    label: "CrossRef",
    external: false,
    href: ({ citation, journalAbbr, articleId }) =>
      citation?.count ? getArticleCrossrefUrl(journalAbbr, articleId) : "#",
  },
  {
    service: "openalex",
    label: "OpenAlex",
    external: false,
    href: ({ citation, journalAbbr, articleId }) =>
      citation?.count ? getArticleOpenAlexUrl(journalAbbr, articleId) : "#",
  },
  {
    service: "opencitations",
    label: "OpenCitations",
    external: false,
    href: ({ citation, journalAbbr, articleId }) =>
      citation?.count
        ? getArticleOpenCitationsUrl(journalAbbr, articleId)
        : "#",
  },
];

const ArticleCitedBy = ({
  journalAbbr,
  articleId,
  citation,
}: {
  journalAbbr: string;
  articleId: number;
  citation: CitationData[];
}) => {
  const getCitationBy = useCallback((citation: CitationData[], key: string) => {
    if (!citation) {
      return undefined;
    }

    return citation.find((article: CitationData) => article.service === key);
  }, []);

  return (
    <Box sx={{
      mb: 1
    }}>
      <StyledDivider label="Cited by" />
      <List
        sx={{
          py: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {SOURCES.map((source) => {
          const sourceCitation = getCitationBy(citation, source.service);

          return (
            <ListItem
              key={source.service}
              sx={{
                py: 0,
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Link
                component={NextLink}
                href={source.href({
                  citation: sourceCitation,
                  journalAbbr,
                  articleId,
                })}
                {...(source.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "underline",
                  textUnderlineOffset: "0.15em",
                }}
              >
                <SpanTypography sx={{
                  fontSize: "0.9rem"
                }}>
                  {source.label}: {sourceCitation?.count ?? 0}
                </SpanTypography>
              </Link>
              <Typography
                variant="subtitle2"
                component="p"
                sx={{
                  color: "text.secondary",
                  fontSize: ".7rem"
                }}>
                {sourceCitation
                  ? `Last Update: ${formatRelativeTime(sourceCitation.updated_at ?? "")}`
                  : ""}
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default ArticleCitedBy;
